<template>
  <div class="min-h-screen" data-theme="red">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="news" />
    <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <a :href="'/news'" class="text-sm text-blue-700 hover:text-blue-900 mb-4 inline-block">&larr; {{ t('news.back_to_list') }}</a>

      <!-- Article -->
      <article class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div class="flex items-start justify-between gap-3 mb-2">
          <h1 class="text-2xl font-bold text-gray-900">{{ newsItem.title }}</h1>
          <div v-if="userAdmin" class="flex gap-2 shrink-0">
            <form :action="`/news/${newsItem.id}/share_telegram`" method="post" class="inline">
              <input type="hidden" name="authenticity_token" :value="csrfToken" />
              <button type="submit" class="px-3 py-1.5 text-xs font-medium text-white bg-[#2AABEE] rounded-lg hover:bg-[#229ED9] transition inline-flex items-center gap-1">
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm5.94 8.13l-1.97 9.28c-.15.67-.54.83-1.09.52l-3.02-2.22-1.46 1.4c-.16.16-.3.3-.61.3l.22-3.06 5.57-5.03c.24-.22-.05-.33-.38-.13l-6.88 4.33-2.97-.93c-.64-.2-.66-.64.14-.95l11.6-4.47c.54-.2 1.01.13.83.95z"/></svg>
                Telegram
              </button>
            </form>
            <a :href="`/news/${newsItem.id}/edit`" class="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition">{{ t('news.edit') }}</a>
            <button @click="deleteNews" class="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition">{{ t('news.delete') }}</button>
          </div>
        </div>
        <p class="text-xs text-gray-500 mb-6">{{ newsItem.user.name }} · {{ formatDate(newsItem.created_at) }}</p>
        <div class="text-gray-700 leading-relaxed news-content" v-html="bodyForDisplay"></div>
        <a v-if="newsItem.forum_topic_id" :href="`/forum/${newsItem.forum_topic_id}`" class="mt-4 inline-block text-sm text-blue-700 hover:underline">{{ t('news.discuss_in_forum') }} &rarr;</a>
      </article>

      <!-- Comments -->
      <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('news.comments') }} ({{ comments.length }})</h3>

      <div class="space-y-4 mb-6">
        <div v-for="msg in comments" :key="msg.id" class="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div class="flex items-start gap-3">
            <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-sm font-medium text-blue-800 shrink-0 mt-0.5">
              {{ msg.user.name.charAt(0).toUpperCase() }}
            </span>
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <span v-if="signedIn && msg.user.id !== userId" @click="openChatWith(msg.user)" class="text-sm font-medium text-gray-900 hover:text-blue-700 cursor-pointer">{{ msg.user.name }}</span>
                <span v-else class="text-sm font-medium text-gray-900">{{ msg.user.name }}</span>
                <span class="text-xs text-gray-400">{{ formatDateTime(msg.created_at) }}</span>
              </div>
              <div v-if="msg.parent_preview" class="mb-1 px-3 py-1.5 bg-gray-50 border-l-2 border-blue-300 rounded text-xs text-gray-500">
                <span class="font-medium text-gray-700">{{ msg.parent_preview.user_name }}</span>: {{ msg.parent_preview.body }}
              </div>
              <p class="text-sm text-gray-700 whitespace-pre-line">{{ msg.body }}</p>
              <div class="mt-2 flex gap-2">
                <button v-if="signedIn" @click="startReply(msg)" class="text-xs text-blue-600 hover:text-blue-800">{{ t('news.reply') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p v-if="comments.length === 0" class="text-center text-gray-400 text-sm mb-6">{{ t('news.no_comments') }}</p>

      <!-- Add comment -->
      <div v-if="signedIn" class="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div v-if="replyTo" class="mb-2 px-3 py-1.5 bg-gray-50 border-l-2 border-blue-300 rounded text-xs text-gray-500 flex items-center justify-between">
          <span><span class="font-medium text-gray-700">{{ replyTo.user.name }}</span>: {{ replyTo.body.substring(0, 80) }}</span>
          <button @click="replyTo = null" class="text-gray-400 hover:text-gray-600 ml-2">&times;</button>
        </div>
        <form @submit.prevent="addComment">
          <div class="flex gap-2 items-center mb-2">
            <input ref="commentInput" v-model="newComment" type="text" :placeholder="replyTo ? t('news.reply_placeholder') : t('news.comment_placeholder')" required
                   class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <div class="relative">
              <button type="button" @click="showEmojis = !showEmojis" class="text-gray-400 hover:text-gray-600 text-xl p-1">😊</button>
              <div v-if="showEmojis" class="absolute bottom-full right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50 w-64 flex flex-wrap gap-1">
                <button v-for="e in emojis" :key="e" type="button" @click="addEmoji(e)" class="text-xl hover:bg-gray-100 rounded p-1 cursor-pointer">{{ e }}</button>
              </div>
            </div>
            <button type="submit" :disabled="!newComment.trim()"
                    class="px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
              {{ t('news.send') }}
            </button>
          </div>
        </form>
      </div>
      <p v-else class="text-center text-sm text-gray-500">
        <a href="/users/sign_in" class="text-blue-700 hover:underline">{{ t('auth.sign_in') }}</a>
        {{ t('news.to_comment') }}
      </p>
    </div>
  </div>
</template>

<style>
.news-content::after { content: ''; display: block; clear: both; }
.news-content img { max-width: 100%; height: auto; border-radius: 0.5rem; margin: 0.5rem 0; }
.news-content h2 { font-size: 1.25rem; font-weight: 700; margin: 0.75rem 0 0.25rem; }
.news-content h3 { font-size: 1.1rem; font-weight: 600; margin: 0.5rem 0 0.25rem; }
.news-content blockquote { border-left: 3px solid #d1d5db; padding-left: 1rem; color: #6b7280; margin: 0.5rem 0; }
.news-content ul { list-style: disc; padding-left: 1.5rem; }
.news-content ol { list-style: decimal; padding-left: 1.5rem; }
.news-content a { color: #2563eb; text-decoration: underline; }
.news-content hr { border-color: #e5e7eb; margin: 1rem 0; clear: both; }
.news-content p { margin: 0.5rem 0; }
</style>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'
import { flattenNewsImagesForDisplay } from './newsBodyHtml.js'

const props = defineProps({ signedIn: Boolean, userName: String, userId: Number, userAdmin: Boolean, newsItem: Object, comments: Array, csrfToken: String })
const { t, locale } = useI18n()

const bodyForDisplay = computed(() => flattenNewsImagesForDisplay(props.newsItem.body || ''))
const showMenu = ref(false)
const logoSize = ref(120)
const msgs = ref([...props.comments])
const newComment = ref('')
const replyTo = ref(null)
const commentInput = ref(null)
const showEmojis = ref(false)
const emojis = ['😊', '😂', '🤣', '😍', '🥰', '😎', '🤔', '😢', '😡', '👍', '👎', '👏', '🙌', '💪', '🏃', '🏃‍♀️', '🏅', '🥇', '🥈', '🥉', '🎉', '🔥', '❤️', '💯', '⭐', '🌟', '☀️', '🌧️', '💨', '🗺️', '📍', '⏱️', '🎯', '✅', '❌', '⚡']

function addEmoji(e) {
  newComment.value += e
  showEmojis.value = false
  if (commentInput.value) commentInput.value.focus()
}

// Use msgs as comments in template
const comments = msgs

function openChatWith(user) {
  window.dispatchEvent(new CustomEvent('sidebar:open-chat', { detail: { id: user.id, name: user.name } }))
}

async function deleteNews() {
  if (!confirm(t('news.confirm_delete_message'))) return
  try {
    const res = await fetch(`/news/${props.newsItem.id}`, {
      method: 'DELETE', headers: { 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' }
    })
    if (res.ok) window.location.href = '/news'
  } catch {}
}

function formatDate(d) { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) }
function formatDateTime(d) { const dt = new Date(d); return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' + dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) }

function startReply(msg) { replyTo.value = msg }

async function request(url, method, body = null) {
  const o = { method, headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' } }
  if (body) o.body = JSON.stringify(body)
  return fetch(url, o)
}

async function addComment() {
  if (!newComment.value.trim()) return
  const payload = { comment: { body: newComment.value } }
  if (replyTo.value) payload.comment.parent_id = replyTo.value.id
  try {
    const res = await request(`/news/${props.newsItem.id}/add_comment`, 'POST', payload)
    if (res.ok) { msgs.value.push(await res.json()); newComment.value = ''; replyTo.value = null }
  } catch {}
}
</script>
